<template>
  <div>
    <div class="pb-4 print:hidden">
      <ClientsFilters @apply-filters="loadClients" @clear-filters="handleClearFilters" />
    </div>

    <div class="sticky z-[3] bg-white print:static" :style="{ top: `${navbarHeight}px` }">
      <div class="flex justify-end gap-2 pb-2 print:hidden">
        <LlSecondaryButton
          variant="outlined"
          append-icon="mdi-printer"
          data-cy="clients-print-button"
          @click="handlePrint"
        >
          Print
        </LlSecondaryButton>
        <LlSecondaryButton
          variant="outlined"
          append-icon="mdi-download"
          :disabled="exporting || loading"
          data-cy="clients-export-button"
          @click="handleExport"
        >
          Export
        </LlSecondaryButton>
        <LlSecondaryButton
          v-if="canValidateQuickbooks"
          variant="outlined"
          append-icon="mdi-cloud-sync-outline"
          :loading="loading"
          :disabled="loading"
          data-cy="clients-validate-quickbooks-button"
          @click="handleValidateQuickbooks"
        >
          Validate QuickBooks Connection
        </LlSecondaryButton>
        <LlSecondaryButton
          color="primary"
          variant="flat"
          data-cy="create-client-button"
          @click="handleCreateClient"
        >
          <LlIcon class="mr-1">mdi-plus</LlIcon>
          Create Client
        </LlSecondaryButton>
      </div>
    </div>

    <!-- The Matters table's look (its :deep rules), as Tailwind variants: new SFCs carry no style block. -->
    <LlDataTable
      :headers="headers"
      :items="clients"
      :items-length="totalCount"
      :loading="loading"
      :sort-by="sortBy"
      item-value="id"
      data-cy="clients-table"
      class="[&_.v-data-table__th]:!bg-neutral-50 [&_.v-data-table__th]:!font-semibold
        [&_.v-data-table__th]:!normal-case [&_.v-data-table__th]:!tracking-normal
        [&_.v-data-table__th]:!text-[0.8125rem] [&_.v-data-table__th]:!text-gray-600
        [&_.v-data-table__th]:!px-4 [&_.v-data-table__th]:!py-3.5
        [&_.v-data-table__th]:!border-b [&_.v-data-table__th]:!border-gray-200
        [&_.v-data-table__td]:!px-4 [&_.v-data-table__td]:!py-3.5
        [&_.v-data-table__td]:!text-[0.8125rem] [&_.v-data-table__td]:!text-gray-900
        [&_.v-data-table__td]:!border-b [&_.v-data-table__td]:!border-gray-100
        [&_tbody_tr]:cursor-pointer [&_tbody_tr:hover]:!bg-gray-50
        print:[&_.v-table__wrapper]:!h-auto print:[&_.v-table__wrapper]:!overflow-visible"
      @update:sort-by="handleSortChange"
      @click:row="handleRowClick"
    >
      <template #[`header.qbo`]>
        <LlTooltip :text="QB_STATUS_TOOLTIP" icon="help" />
      </template>
      <template #[`item.qbo`]="{ item }">
        <LlIcon
          v-if="item.quickBooksId"
          icon="mdi-circle"
          :color="item.goodQBConnection ? 'success' : 'error'"
          size="10"
        />
      </template>

      <template #[`item.clientId`]="{ item }">
        <span class="text-gray-700 text-sm">{{ formatOrDefault(item.reference) }}</span>
      </template>

      <template #[`item.clientName`]="{ item }">
        <span class="text-gray-900 text-sm font-medium">{{ item.name }}</span>
      </template>

      <template #[`item.companyName`]="{ item }">
        <span class="text-gray-700 text-sm">{{ formatOrDefault(item.companyName, '—') }}</span>
      </template>

      <template #[`item.contactName`]="{ item }">
        <span class="text-gray-700 text-sm">
          {{ formatOrDefault(formatContactName(item.contactName), '—') }}
        </span>
      </template>

      <template #[`item.phone`]="{ item }">
        <span class="text-gray-700 text-sm">{{ formatOrDefault(item.phone, '—') }}</span>
      </template>

      <template #[`item.primary`]="{ item }">
        <div v-if="item.primary.length" class="flex flex-wrap gap-1">
          <LlUserAvatar v-for="initials in item.primary" :key="initials" :initials="initials" />
        </div>
        <span v-else class="text-gray-400">—</span>
      </template>

      <template #[`item.actions`]="{ item }">
        <LlMenu location="bottom end" :items="rowMenuItems(item)">
          <template #activator="{ props: menuProps }">
            <LlSecondaryButton
              v-bind="menuProps"
              icon="mdi-dots-vertical"
              variant="text"
              size="small"
              density="comfortable"
              :aria-label="`Actions for ${item.name}`"
              data-cy="client-row-menu"
            />
          </template>
        </LlMenu>
      </template>

      <template #loading>
        <div class="p-8">
          <v-skeleton-loader type="table-row@10" class="w-full" />
        </div>
      </template>

      <template #no-data>
        <div class="text-center py-16" data-cy="clients-empty">
          <LlIcon icon="mdi-account-outline" size="64" color="grey-lighten-2" class="mb-4" />
          <p class="text-xl font-medium text-gray-600 mb-2">No clients match the filters</p>
        </div>
      </template>
    </LlDataTable>

    <LlDataTablePagination
      sticky
      class="print:hidden"
      :page="filters.page"
      :page-size="filters.pageSize"
      :total-count="totalCount"
      @update:page="handlePageChange"
      @update:page-size="handlePageSizeChange"
    />

    <LlConfirmationDialog
      ref="deleteConfirmRef"
      title="Delete this client?"
      :message="deleteConfirmMessage"
      confirm-text="Delete"
      :show-warning-icon="false"
      confirm-color="error"
      confirm-variant="flat"
      data-cy="client-delete-dialog"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { PropType } from 'vue';
import { storeToRefs } from 'pinia';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlDataTable from '@/components/LlDataTable.vue';
import LlDataTablePagination from '@/components/LlDataTablePagination.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlMenu from '@/components/LlMenu.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlUserAvatar from '@/components/LlUserAvatar.vue';
import { useExportUtilities } from '@/composables/useExportUtilities';
import { useNavbarHeight } from '@/composables/useNavbarHeight';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import type { SortByItem } from '@/models/llDataTableTypes';
import RouteService from '@/services/RouteService';
import type { UiRouterState } from '@/services/RouteService/UiRouterState';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { formatOrDefault } from '@/services/utils';
import ClientsFilters from './ClientsFilters.vue';
import { QB_STATUS_TOOLTIP } from '../constants/clientsListConstants';
import type { ClientRow, ClientsOrderBy } from '../models/clientsListTypes';
import { useClientsListStore } from '../stores/useClientsListStore';
import { formatContactName } from '../utils/clientsListHelpers';

const props = defineProps({
  state: { type: Object as PropType<UiRouterState>, required: false },
});

const clientsListStore = useClientsListStore();
const { filters, clients, totalCount, loading, exporting } = storeToRefs(clientsListStore);
const meStore = useMeStore();
const { me, terms, permissions, company, access, settings, features } = storeToRefs(meStore);
const accountsStore = useAccountsStore();
const { accountsList } = storeToRefs(accountsStore);

const { handleError, handleSuccess, handleWarning } = useSnackbarHandler();
const { exportCSV } = useExportUtilities();
const routeService = new RouteService(props);
const navbarHeight = useNavbarHeight();

const deleteConfirmRef = ref<{ open: () => Promise<boolean> } | null>(null);
const clientToDelete = ref<ClientRow | null>(null);

const matterTerm = computed(() => terms.value?.Matter || 'Matter');

// The legacy page's gates: its QuickBooks panel needs matters.accounting, and the
// validate button inside it also needs billing access on a connected company.
const canViewInQuickbooks = computed(() => !!permissions.value?.matters?.accounting);
const canValidateQuickbooks = computed(
  () => canViewInQuickbooks.value && !!company.value?.integration?.quickbooks && !!access.value?.billing,
);
const usesNewTimeEntry = computed(
  () => !!features.value?.timeEntriesTab && !!settings.value?.enableNewTimeEntry,
);

const PRINT_HIDDEN = { class: 'print:hidden' };

const headers = computed(() => [
  // Only once the connection check has run; before that every cell would be blank.
  ...(filters.value.checkQuickbooks
    ? [{ title: '', key: 'qbo', sortable: false, width: 30, headerProps: PRINT_HIDDEN, cellProps: PRINT_HIDDEN }]
    : []),
  { title: 'Client ID', key: 'clientId', value: 'reference', sortable: true, width: 100 },
  { title: 'Client', key: 'clientName', value: 'name', sortable: true },
  { title: 'Company', key: 'companyName', sortable: true },
  { title: 'Contact', key: 'contactName', sortable: true },
  { title: 'Phone', key: 'phone', sortable: true },
  ...(filters.value.orphans ? [] : [{ title: 'Responsible', key: 'primary', sortable: true }]),
  { title: '', key: 'actions', sortable: false, width: 50, headerProps: PRINT_HIDDEN, cellProps: PRINT_HIDDEN },
]);

const sortBy = computed<SortByItem[]>(() => [
  { key: filters.value.orderBy, order: filters.value.sortAscending ? 'asc' : 'desc' },
]);

const loadClients = async () => {
  try {
    await clientsListStore.loadClients();
  } catch (error) {
    handleError(error);
  }
};

const loadCustomFields = async () => {
  try {
    await clientsListStore.loadCustomFields();
  } catch (error) {
    handleError(error);
  }
};

const handleSortChange = (newSort: SortByItem[]) => {
  if (!newSort.length) return;
  filters.value.orderBy = newSort[0].key as ClientsOrderBy;
  filters.value.sortAscending = newSort[0].order === 'asc';
  filters.value.page = 1;
  loadClients();
};

const handlePageChange = (page: number) => {
  filters.value.page = page;
  loadClients();
};

const handlePageSizeChange = (pageSize: number) => {
  filters.value.pageSize = pageSize;
  filters.value.page = 1;
  loadClients();
};

const handleClearFilters = () => {
  clientsListStore.resetFilters();
  loadClients();
};

const handleValidateQuickbooks = () => {
  filters.value.checkQuickbooks = true;
  filters.value.page = 1;
  loadClients();
};

const handlePrint = () => {
  window.print();
};

const handleExport = async () => {
  try {
    const rows = await clientsListStore.fetchAllClients();
    await exportCSV(
      rows,
      {
        'Client Id': 'reference',
        Client: 'name',
        Company: 'companyName',
        Contact: (item: ClientRow) => formatContactName(item.contactName),
        Phone: 'phone',
        Responsible: (item: ClientRow) => item.primary.join(' '),
      },
      { filename: 'Clients' },
    );
  } catch (error) {
    handleError(error);
  }
};

const handleCreateClient = () => {
  routeService.go(usesNewTimeEntry.value ? 'clients2.client2' : 'clients2.newMatter.client', {
    methodOverride: 'client page',
  });
};

const createMatterFor = (item: ClientRow) => {
  routeService.go(usesNewTimeEntry.value ? 'clients2.matter2' : 'clients2.newMatter', {
    clientId: item.id,
  });
};

const openClient = (item: ClientRow) => {
  // The client page assumes at least one matter and breaks without one.
  if (filters.value.orphans) {
    handleWarning(`Please create a ${terms.value?.matter || 'matter'} for the client to start using it in the system`);
    return;
  }
  routeService.go('clients2.client', { id: item.id });
};

const handleRowClick = (event: MouseEvent, { item }: { item: ClientRow }) => {
  if ((event.target as HTMLElement).closest('button, a')) return;
  openClient(item);
};

const openInQuickbooks = (item: ClientRow) => {
  const base = me.value?.configuration?.quickbooks;
  if (!base || !item.quickBooksId) return;
  window.open(
    `${base}/app/customerdetail?nameId=${encodeURIComponent(item.quickBooksId)}`,
    '_blank',
    'noopener,noreferrer',
  );
};

const deleteConfirmMessage = computed(() =>
  clientToDelete.value
    ? `Should the client "${clientToDelete.value.label}" be removed from the system? It doesn't have any ${terms.value?.matter || 'matter'}s or other data associated with it, but it cannot be undone.`
    : '',
);

const confirmDelete = async (item: ClientRow) => {
  clientToDelete.value = item;
  const confirmed = await deleteConfirmRef.value?.open();
  if (!confirmed) return;
  try {
    await clientsListStore.deleteClient(item.id);
    handleSuccess(`The client "${item.label}" was removed`);
    await loadClients();
  } catch (error) {
    handleError(error);
  }
};

const rowMenuItems = (item: ClientRow) => {
  if (filters.value.orphans) {
    return [
      {
        id: 'create-matter',
        title: `Create ${matterTerm.value}`,
        dataCy: 'client-row-create-matter',
        action: () => createMatterFor(item),
      },
      { id: 'delete', title: 'Delete', dataCy: 'client-row-delete', action: () => confirmDelete(item) },
    ];
  }
  return [
    { id: 'open', title: 'Open', dataCy: 'client-row-open', action: () => openClient(item) },
    ...(item.quickBooksId && canViewInQuickbooks.value
      ? [
        {
          id: 'quickbooks',
          title: 'View in QuickBooks',
          dataCy: 'client-row-view-quickbooks',
          action: () => openInQuickbooks(item),
        },
      ]
      : []),
  ];
};

onMounted(async () => {
  try {
    await Promise.all([meStore.getMe(), accountsStore.listAccounts()]);
  } catch (error) {
    handleError(error);
  }
  const ownAccount = (accountsList.value || []).find((account: { id: string }) => account.id === me.value?.id);
  clientsListStore.applyRoleDefaults(me.value?.role, ownAccount);
  loadClients();
  if (features.value?.customFieldFilters) {
    loadCustomFields();
  }
});

onBeforeUnmount(() => {
  clientsListStore.reset();
});
</script>
